import Superscript from '@tiptap/extension-superscript';
import { Table } from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import CharacterCount from '@tiptap/extension-character-count';
import { CustomHeading } from './CustomHeading';
import { CustomImage } from './CustomImage';
import { CustomParagraph } from './CustomParagraph';
import { CustomTable } from './CustomTable';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Extension } from '@tiptap/core';
import { DOMParser } from '@tiptap/pm/model';

// 创建专门处理图片渲染的插件
const createImageRenderPlugin = () => {
    return new Plugin({
        key: new PluginKey('imageRenderFix'),
        props: {
            handleDOMEvents: {
                // 监听文档加载完成事件
                load: (view, event) => {
                    // 延迟执行以确保DOM已完全渲染
                    setTimeout(() => {
                        // 查找所有图片节点
                        const imageNodes = view.dom.querySelectorAll('img');

                        // 处理每个图片节点
                        imageNodes.forEach((img) => {
                            // 检查图片是否已加载
                            if (!img.complete || img.naturalWidth === 0) {
                                console.log('发现未加载的图片:', img.src);

                                // 处理base64内容
                                if (
                                    img.src &&
                                    img.src.length > 100 &&
                                    !img.src.startsWith('data:')
                                ) {
                                    // 尝试转换为data URI格式
                                    const newSrc = `data:image/png;base64,${img.src}`;
                                    img.src = newSrc;
                                }

                                // 监听图片加载完成事件
                                img.onload = () => {
                                    console.log('图片加载成功:', img.src);
                                };

                                img.onerror = () => {
                                    console.error('图片加载失败:', img.src);
                                };
                            }
                        });
                    }, 500);

                    return false; // 不阻止事件继续传播
                },
            },
        },
    });
};

// 创建自定义HTML解析器扩展
const CustomHTMLParser = Extension.create({
    name: 'customHTMLParser',

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey('customHTMLParser'),
                props: {
                    // 覆盖默认的transformPastedHTML处理
                    transformPastedHTML(html) {
                        // 在粘贴处理前对图片标签进行预处理
                        return html.replace(/<img(.*?)>/g, (match, attributes) => {
                            // 确保图片标签有关闭标签，防止被解析为无效节点
                            return `<img${attributes}></img>`;
                        });
                    },
                    // 覆盖默认的parseDOM处理
                    parseDOM: (element, schema) => {
                        // 将所有br.ProseMirror-trailingBreak替换为实际的图片
                        const brNodes = element.querySelectorAll('br.ProseMirror-trailingBreak');
                        brNodes.forEach((br) => {
                            // 查找前面的注释节点，可能包含原始的图片标签
                            let node = br.previousSibling;
                            while (node) {
                                if (node.nodeType === Node.COMMENT_NODE) {
                                    // 检查注释中是否包含img标签
                                    const comment = node.textContent;
                                    if (comment && comment.includes('<img')) {
                                        // 创建一个新的img元素
                                        const temp = document.createElement('div');
                                        temp.innerHTML = comment;
                                        const img = temp.querySelector('img');
                                        if (img) {
                                            // 替换br标签为img
                                            br.parentNode.replaceChild(img, br);
                                            // 删除注释节点
                                            node.parentNode.removeChild(node);
                                            console.log('成功恢复图片节点');
                                            break;
                                        }
                                    }
                                }
                                node = node.previousSibling;
                            }
                        });

                        // 使用默认解析器
                        return DOMParser.fromSchema(schema).parse(element);
                    },
                },
            }),
        ];
    },
});

/**
 * Tiptap 编辑器扩展配置
 * 包含了编辑器所需的所有功能扩展
 */
export const extensions = [
    // 上标扩展，用于处理引用标记
    Superscript,

    // 基础功能套件，包含了常用的编辑功能
    StarterKit.configure({
        heading: {
            levels: [1, 2, 3, 4], // 支持1-4级标题
        },
    }),

    // 图片扩展配置
    Image.configure({
        HTMLAttributes: {
            inline: false, // 不允许行内图片
            contenteditable: false, // 图片不可编辑
            id: null, // 图片ID属性
            decoding: 'async', // 异步解码图片以提高性能
        },
        resizable: false, // 禁用图片缩放
        draggable: false, // 禁用图片拖拽
        allowBase64: true, // 允许Base64图片
        parseHTML() {
            return [
                {
                    tag: 'img[src]', // 匹配任何有src属性的img标签
                    priority: 100, // 提高优先级，确保图片解析优先于其他规则
                    getAttrs: (dom) => ({
                        src: dom.getAttribute('src'),
                        alt: dom.getAttribute('alt'),
                        title: dom.getAttribute('title'),
                        id: dom.getAttribute('id'),
                        class: dom.getAttribute('class'),
                    }),
                },
                {
                    tag: 'img.aiml-chart', // 特定类的图片
                    priority: 101,
                },
                {
                    tag: 'img.aiml-mermaid', // 特定类的图片
                    priority: 101,
                },
                {
                    tag: 'img.aiml-latex', // 特定类的图片
                    priority: 101,
                },
            ];
        },
    }),

    // // 表格扩展配置
    // Table.configure({
    //     HTMLAttributes: {
    //         class: 'academic-table',
    //     },
    //     resizable: false, // 禁用表格缩放
    //     draggable: false, // 禁用表格拖拽
    // }),

    // 表格行配置
    TableRow.configure({
        HTMLAttributes: {
            class: 'academic-table-row',
        },
    }),

    // 表格头部配置
    TableHeader.configure({
        HTMLAttributes: {
            class: 'academic-table-header',
        },
    }),

    // 表格单元格配置
    TableCell.configure({
        HTMLAttributes: {
            class: 'academic-table-cell',
        },
    }),

    // 自定义扩展
    CustomHeading, // 自定义标题组件
    CustomParagraph, // 自定义段落组件
    CustomImage, // 自定义图片组件
    CustomTable, // 自定义表格组件
    // 字数统计扩展
    CharacterCount.configure({
        count: (text) => {
            // 移除空格和换行符后计算字数
            return text.replace(/\s+/g, '').length;
        },
    }),

    // 添加图片渲染修复插件
    createImageRenderPlugin(),

    // 添加自定义HTML解析器
    CustomHTMLParser,
];
